import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { GlobeIcon } from '@repo/ui/icons'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { getCountriesList, getCountriesListQueryKey } from '../../api/countries/get-countries-list'
import { getCountryFlag } from '../../lib/utils/functions/country.functions'

type Props = {
  code?: string
  label?: string
  size?: 'regular' | 'large'
  disableLoadingState?: boolean

  /**
   * If the country is not in the list, use this prop to display the flag
   */
  possibleCountry?: string
}

const CountryFlag: React.FC<Props> = ({
  size = 'regular',
  code,
  label,
  possibleCountry,
  disableLoadingState = false
}) => {
  const { data: countryList, isLoading } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList(),
    staleTime: Infinity
  })

  // Show skeleton while loading
  if (isLoading && !disableLoadingState) {
    return (
      <div className={'flex items-center gap-2'}>
        <Skeleton
          className={cn('rounded-full w-4 h-4', {
            'scale-100': size === 'regular',
            'scale-125': size === 'large'
          })}
        />
        {label && <Skeleton className="h-4 w-16" />}
      </div>
    )
  }

  if (!countryList) return null

  if (possibleCountry) {
    const countryData = getCountryFlag(possibleCountry, countryList.data)
    if (countryData) code = countryData.key
  }

  // check if the code is an alpha2 code
  if (code && code.length === 2) {
    // check if the code is in the country list
    const countryData = countryList.data.find((country) => country.alpha2 === code)
    // if it is, set the code to the key
    if (countryData) code = countryData.key
  }

  return (
    <div className={'flex items-center gap-2'}>
      <div className="h-4 w-4">
        <>
          {code &&
            (countryList.data.find((country) => country.key === code) ? (
              <img
                src={countryList.mapping[code]}
                alt={'flag'}
                className={cn('w-full h-full rounded-full', {
                  'scale-100': size === 'regular',
                  'scale-125': size === 'large'
                })}
              />
            ) : (
              <GlobeIcon
                className={'w-full h-full rounded-full scale-110'}
                color={tailwindColors.blue.DEFAULT}
              />
            ))}
          {!code && possibleCountry && (
            <GlobeIcon
              className={'w-full h-full rounded-full scale-110'}
              color={tailwindColors.blue.DEFAULT}
            />
          )}
        </>
      </div>

      {label && <div>{label}</div>}
    </div>
  )
}

export default CountryFlag
